import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { CancelToken } from 'axios';
import { usePromise } from '../../../Common/hooks/usePromise';
import { ErrorPage } from '../../../HubLayout/components/ErrorPage';
import { PageHeader } from '../../../HubLayout/components/PageLayout/PageHeader';
import { ReleaseFlow } from './ReleaseFlow';
import {
    getNewYamlReleaseUrl,
    getAdoAccountUrl,
    getAdoProjectUrl,
    getServiceTreeUrl,
    ServiceReleaseDetailsRouteParams,
} from '../../../Common/util/RouteUtils';
import { useParams } from 'react-router-dom';
import { useAsState } from '../../../Common/hooks/useAsState';
import { getReleaseDetails } from '../../api/AGSSApi';
import { CompletionStatusDisplay } from './Display/Status/CompletionStatusDisplay';
import {
    IContextualMenuItem,
    IStackTokens,
    Link,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Stack,
} from '@fluentui/react';
import { PageBreadcrumbTrail } from '../../../HubLayout/components/PageLayout/PageBreadcrumbTrail';
import { PageType } from '../../../HubLayout/models/Page';
import { LinkItem } from '../../../HubLayout/models/LinkItem';
import { spinnerStyles, stackStyles } from '../../util/StyleUtils';
import useDocumentTitle from '../../../Common/hooks/useDocumentTitle';
import { ReleasePipelineSource } from '../../models/ReleasePipelineSource';
import { Release } from '../../models/Release';
import { emitPageLoadMetric } from '../../../Common/util/metricsUtil';
import { Capability } from '../../../Common/components/Capabilities/capability';
import { CapabilityContext } from '../../../Common/components/Capabilities/CapabilityContext';
import { BackButton } from '../../../HubLayout/components/Utilities/BackButton';
import { ReleaseExporter } from '../../../Administration/components/Importer/ReleaseExporter';
import { CopyReleaseDataButton } from './CopyReleaseDataButton';
import { setUserProfileData } from '../../../Common/api/ABHub/UserProfileApi';
import { AuthContext } from '../../../HubLayout/models/AuthContext';
import { useIsMobile } from '../../../Common/hooks/useIsMobile';
import { MobileReleaseDetailsPage } from './MobileReleaseDetailsPage';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../Common/api/ABHub/ABHubGlobalConfig';
import {
    AgrmType,
    checkSreDeploymentControl,
    isValidCloudOrEnvironment,
} from '../../api/AGRMApi';
import { SREControlModal } from './SreControlModal';
import { Ellipsis } from '../../../HubLayout/components/Utilities/Ellipsis';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { AutoRefresh } from './AutoRefresh';

export interface IReleaseDetailsProps {}

export const ReleaseDetailsPage: React.FC<IReleaseDetailsProps> = (
    props: IReleaseDetailsProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const capabilities = useContext(CapabilityContext);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
    const routeParams = useParams<ServiceReleaseDetailsRouteParams>();
    const params = useAsState<ServiceReleaseDetailsRouteParams>(routeParams); // fixes timing bug on routeParam change
    const isMobile = useIsMobile();

    // Raw release JSON captured on load (pre-transformation) for admin copy.
    const [rawReleaseJson, setRawReleaseJson] = useState<string | undefined>(
        undefined
    );

    // SRE Cancel Control variables
    const environment = getConfig(GlobalConfigProperties.Environment);
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const [validEnv, setValidEnv] = useState<boolean>(true);
    const [showSREControlModal, setShowSREControlModal] = useState(false);
    const [specificUniqueId, setSpecificUniqueId] = useState<string>('');
    const [controlWasUpdated, setControlWasUpdated] = useState(false);
    const [doesControlExist, setDoesControlExist] = useState(false);

    const controlCheckCallback = useCallback(
        async (cancelToken?: CancelToken) => {
            if (specificUniqueId) {
                const result = await checkSreDeploymentControl(
                    specificUniqueId,
                    AgrmType.Deployment,
                    cancelToken
                );

                setDoesControlExist(result.data ?? false);

                return result.data;
            }
        },
        [specificUniqueId]
    );

    const [
        controlAlreadyExists,
        sreControlError,
        sreControlisLoaded,
        startControlCheck,
    ] = usePromise(controlCheckCallback, false);

    // adds this page to the recent releases list
    const { authContext, setDbUpdated, isUpdated } = useContext(AuthContext);

    const setRecentlyViewedRelease = useCallback(
        async (cancelToken?: CancelToken) => {
            if (authContext.userProfile) {
                const response = await setUserProfileData(
                    authContext.userProfile?.alias,
                    params.releaseCorrelationId,
                    cancelToken
                );

                if (setDbUpdated) {
                    setDbUpdated(true);
                }

                return response;
            }
        },
        [authContext, authContext.userProfile]
    );

    const [recentData, recentError, recentLoading, recentStart] = usePromise(
        setRecentlyViewedRelease,
        true
    );

    const getData = useCallback(
        async (cancelToken?: CancelToken) => {
            if (params.releaseCorrelationId) {
                //query for specify releaseCorrelationId
                return await getReleaseDetails(
                    params.releaseCorrelationId,
                    cancelToken,
                    setRawReleaseJson
                );
            }
            return undefined;
        },
        // When dependency array changes, usePromise will clear data and restart API.
        [params.releaseCorrelationId] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const [data, error, isLoaded] = usePromise(getData, true);

    // Holds data from an in-place (silent) refresh so fresh release data can be
    // swapped in without clearing the page (which would flash a spinner). Falls
    // back to the initial usePromise data until the first silent refresh lands.
    const [liveRelease, setLiveRelease] = useState<
        { releaseCorrelationId: string; release: Release } | undefined
    >(undefined);

    // Ensure we don't keep rendering a previous release after navigating.
    useEffect(() => {
        setLiveRelease(undefined);
    }, [params.releaseCorrelationId]);

    const release =
        liveRelease?.releaseCorrelationId === params.releaseCorrelationId
            ? liveRelease.release
            : data;

    // Silent, in-place refresh used by the auto-refresh timer and by child
    // actions (e.g. redeploy/restart). Fetches fresh release data and swaps it
    // in only on success, so the page never blanks out to a spinner. Transient
    // failures keep the current data visible.
    const silentRefreshInFlight = useRef(false);
    const silentRefreshRelease = useCallback(async () => {
        if (!params.releaseCorrelationId || silentRefreshInFlight.current) {
            return;
        }
        silentRefreshInFlight.current = true;
        try {
            const fresh = await getReleaseDetails(
                params.releaseCorrelationId,
                undefined,
                setRawReleaseJson
            );
            if (fresh) {
                setLiveRelease({
                    releaseCorrelationId: params.releaseCorrelationId,
                    release: fresh,
                });
            }
        } catch {
            // Keep showing the existing data on a transient refresh failure.
        } finally {
            silentRefreshInFlight.current = false;
        }
    }, [params.releaseCorrelationId]);

    const releasePipelineSource = (): string => {
        if (release) {
            if (
                release.releasePipelineSource === ReleasePipelineSource.Classic
            ) {
                return 'Classic';
            } else if (
                release.releasePipelineSource === ReleasePipelineSource.YAML
            ) {
                return 'YAML';
            }
        }
        return '';
    };

    const createMenuItems = (): IContextualMenuItem[] => {
        const items: IContextualMenuItem[] = [];

        if (
            capabilities.check(Capability.srmAdminRole) &&
            validEnv &&
            doesControlExist !== undefined
        ) {
            items.push({
                key: 'deploymentControl',
                text: `${
                    doesControlExist ? 'Remove' : 'Create'
                } Deployment Control`,
                iconProps: {
                    iconName: `${doesControlExist ? 'Cancel' : 'Shield'}`,
                    styles: {
                        root: {
                            color: doesControlExist
                                ? theme.palette.red
                                : theme.palette.themePrimary,
                        },
                    },
                },
                onClick: () => setShowSREControlModal(true),
            });
        }

        return items;
    };

    const menuItems = createMenuItems();

    // Tokens definition
    const stackTokens: IStackTokens = { childrenGap: 10 };

    // Style definitions
    const midStackStyle = {
        display: 'flex',
        alignItems: 'center',
        marginTop: '-5px',
        marginLeft: '-2px',
    };

    const bottomStackStyle = {
        marginTop: '-15px',
        marginLeft: '-10px',
        justifyContent: 'space-between',
    };

    const releaseButtonDiv = {
        scale: '70%',
        marginTop: '7px',
        marginLeft: '-10px',
    };

    const backButtonStyle = {
        scale: '70%',
    };

    const noLeftMargin = {
        marginLeft: '0px',
    };

    const releaseFlowStyle = {
        marginTop: '10px',
    };

    const getTitle = (): string => {
        if (release) {
            if (release.releaseName) {
                return release.releaseName + ' (Id: ' + release.releaseId + ')';
            }
            return 'Release Details (Id: ' + release.releaseId + ')';
        }
        return 'Release Details';
    };

    useDocumentTitle(getTitle());

    const getAdoReleaseItem = (): LinkItem[] => {
        const releaseItem: LinkItem = {
            text: release?.releaseId?.toString() || '',
            href: release?.releaseWebUrl || '',
            iconName: PageType.Release,
            title: 'Open Azure DevOps Release Pipeline',
        };
        return [releaseItem];
    };

    const getNewYAMLLink = (): LinkItem[] => {
        const releaseItem: LinkItem = {
            text: release?.releaseId?.toString() || '',
            href: getNewYamlReleaseUrl(
                release?.accountName || release?.adoInstance || '',
                release?.projectName || '',
                '',
                release?.releaseId || 0
            ),
            iconName: PageType.Release,
            title: 'Open Azure DevOps Release Pipeline',
        };
        return [releaseItem];
    };

    const getServiceItem = (): LinkItem[] => {
        const serviceItem: LinkItem = {
            text: release?.serviceTreeName || 'Service',
            href: getServiceTreeUrl(release?.serviceTreeId || ''),
            iconName: PageType.Service,
            title: 'Open Service Tree profile',
        };
        return [serviceItem];
    };

    const getAccountProjectItems = (): LinkItem[] => {
        const accountItem: LinkItem = {
            text: release?.adoInstance || 'Account',
            href: getAdoAccountUrl(release?.adoInstance || ''),
            iconName: PageType.Account,
            title: 'Open Azure DevOps Account',
        };
        const projectItem: LinkItem = {
            text: release?.projectName || 'Project',
            href: getAdoProjectUrl(
                release?.accountName || release?.adoInstance || '',
                release?.projectName || '',
                ''
            ),
            iconName: PageType.Project,
            title: 'Open Azure DevOps Project',
        };
        return [accountItem, projectItem];
    };

    useEffect(() => {
        if (isUpdated) {
            recentStart();
        }
    }, [recentData, recentLoading, recentStart, isUpdated]);

    // metric to measure inital page load time
    useEffect(() => {
        var emittedSuccessfully = emitPageLoadMetric(
            'ReleaseDetails',
            process.env.REACT_APP_BUILD_VERSION,
            isFirstLoad
        );

        setIsFirstLoad(emittedSuccessfully);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setValidEnv(isValidCloudOrEnvironment(cloud, environment));
    }, [cloud, environment]);

    useEffect(() => {
        if (validEnv) {
            const uniqueId =
                release?.stages[0]?.deployments[0]?.uniqueIdentifier;

            if (uniqueId) {
                setSpecificUniqueId(uniqueId);
            }
        }
    }, [validEnv, specificUniqueId, release]);

    useEffect(() => {
        if (validEnv && specificUniqueId) {
            startControlCheck();
        }
    }, [validEnv, specificUniqueId, startControlCheck]);

    useEffect(() => {
        if (controlWasUpdated) {
            controlCheckCallback();
            setControlWasUpdated(false);
        }
    }, [controlWasUpdated, controlCheckCallback]);

    return error ? (
        <ErrorPage error={error} />
    ) : (
        <div className="ReleaseDetailsPage-root">
            {isMobile ? (
                <MobileReleaseDetailsPage
                    pageTitle={getTitle()}
                    release={release}
                    isLoaded={isLoaded}
                />
            ) : (
                <div>
                    <Stack
                        horizontal
                        verticalAlign="start"
                        style={{ justifyContent: 'space-between' }}
                    >
                        <PageHeader title={getTitle()} />
                        <AutoRefresh onRefresh={silentRefreshRelease} />
                    </Stack>
                    {isLoaded && (
                        <Stack tokens={stackTokens}>
                            <Stack
                                tokens={stackTokens}
                                horizontal
                                style={midStackStyle}
                            >
                                {/* Show overall release status */}
                                {release && release.completionIndicator && (
                                    <CompletionStatusDisplay
                                        completionIndicator={
                                            release.completionIndicator
                                        }
                                        completionStatusTracker={
                                            release.completionStatusTracker
                                        }
                                        addPadding={true}
                                    />
                                )}

                                {capabilities.check(
                                    Capability.srmAdminRole
                                ) && (
                                    <div style={releaseButtonDiv}>
                                        <ReleaseExporter
                                            releaseCorrelationIds={[
                                                params.releaseCorrelationId,
                                            ]}
                                        />
                                    </div>
                                )}

                                {capabilities.check(
                                    Capability.srmAdminRole
                                ) && (
                                    <div style={releaseButtonDiv}>
                                        <CopyReleaseDataButton
                                            releaseJson={rawReleaseJson}
                                        />
                                    </div>
                                )}
                            </Stack>
                            <Stack
                                horizontal
                                tokens={stackTokens}
                                className={stackStyles.centeredStack}
                                style={bottomStackStyle}
                            >
                                <Stack horizontal>
                                    <div style={backButtonStyle}>
                                        <BackButton />
                                    </div>
                                    {release?.releaseId && (
                                        <Stack horizontal style={noLeftMargin}>
                                            <PageBreadcrumbTrail
                                                inputItems={
                                                    release.releasePipelineSource !==
                                                    ReleasePipelineSource.YAML
                                                        ? getAdoReleaseItem()
                                                        : getNewYAMLLink()
                                                }
                                                label={
                                                    releasePipelineSource() +
                                                    ' ADO Release'
                                                }
                                            />
                                            <PageBreadcrumbTrail
                                                inputItems={[
                                                    {
                                                        text: '|',
                                                        href: '',
                                                        iconName: '',
                                                    },
                                                ]}
                                                label=""
                                            />
                                        </Stack>
                                    )}

                                    <PageBreadcrumbTrail
                                        inputItems={getServiceItem()}
                                        label="Service Tree"
                                    />
                                    <PageBreadcrumbTrail
                                        inputItems={[
                                            {
                                                text: '|',
                                                href: '',
                                                iconName: '',
                                            },
                                        ]}
                                        label=""
                                    />
                                    <PageBreadcrumbTrail
                                        inputItems={getAccountProjectItems()}
                                        label="ADO Hierarchy"
                                    />
                                </Stack>

                                <div>
                                    {menuItems.length > 0 && (
                                        <Ellipsis menuItems={menuItems} />
                                    )}
                                    {showSREControlModal && (
                                        <SREControlModal
                                            isModalOpen={showSREControlModal}
                                            setIsModalOpen={
                                                setShowSREControlModal
                                            }
                                            uniqueIdentifier={specificUniqueId}
                                            type={AgrmType.Deployment}
                                            controlUpdated={
                                                setControlWasUpdated
                                            }
                                        />
                                    )}
                                </div>
                            </Stack>

                            {doesControlExist && (
                                <MessageBar
                                    messageBarType={MessageBarType.warning}
                                    isMultiline={false}
                                    dismissButtonAriaLabel="Close"
                                >
                                    There is currently a deployment control in
                                    place for this release. ** All deployments
                                    across all stages ** that are found while it
                                    is in place will be cancelled, and their
                                    statuses will be updated accordingly. Remove
                                    the deployment control to allow new
                                    orchestrations to proceed.
                                </MessageBar>
                            )}

                            {release?.releasePipelineSource ===
                                ReleasePipelineSource.Classic && (
                                <MessageBar
                                    messageBarType={MessageBarType.warning}
                                    isMultiline={false}
                                >
                                    Classic release pipelines are{' '}
                                    <strong>deprecated</strong>. Migrating to{' '}
                                    <strong>YAML</strong> pipelines is
                                    recommended. For more information, please
                                    refer to the
                                    <Link
                                        href={
                                            process.env
                                                .REACT_APP_CLASSIC_DEPRECATION_GUIDE_URL
                                        }
                                    >
                                        OneBranch YAML documentation
                                    </Link>
                                    .
                                </MessageBar>
                            )}

                            <div style={releaseFlowStyle}>
                                {/* There will be one of these per flow */}
                                <ReleaseFlow
                                    release={release}
                                    isLoaded={isLoaded}
                                    onRefreshReleaseData={silentRefreshRelease}
                                />
                            </div>
                        </Stack>
                    )}
                    {!isLoaded && (
                        <div>
                            <Spinner
                                styles={spinnerStyles}
                                size={SpinnerSize.large}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
